import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { SITE_URL } from "@/lib/seo";

const TITLE = "How DrHint Calculates Gold & Silver Rates — Pricing Methodology";
const DESCRIPTION =
  "The exact formula, data inputs, refresh cadence, known limitations and correction policy behind every gold and silver rate published on DrHint.";
const CANONICAL = `${SITE_URL}/methodology`;

export const Route = createFileRoute("/methodology")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow,max-snippet:-1" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: MethodologyPage,
});

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="mt-10 text-xl md:text-2xl font-bold scroll-mt-24">
      {children}
    </h2>
  );
}

function MethodologyPage() {
  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-2xl md:text-3xl font-black leading-tight">
          How DrHint calculates gold and silver rates
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground/80 leading-relaxed">
          Every rate page on this site is generated from the same pricing
          pipeline. This page documents that pipeline in full: what the inputs
          are, how the arithmetic works, how often it runs, where it is known
          to be imprecise, and what we do when a reader tells us a number is
          wrong. If anything below does not match what you see on a rate page,
          the page is wrong and we want to hear about it.
        </p>

        <H2 id="inputs">The two live inputs</H2>
        <p className="mt-2 text-sm md:text-base text-foreground/85 leading-relaxed">
          Only two values change during the day:
        </p>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm md:text-base text-foreground/85">
          <li>
            <strong>International spot price</strong> — the price of one troy
            ounce of 24-karat gold (or silver) in US dollars on the global
            over-the-counter market. This is the same benchmark London and
            COMEX quote against.
          </li>
          <li>
            <strong>USD exchange rate</strong> — the interbank US dollar rate
            against the Pakistani rupee or Indian rupee.
          </li>
        </ol>
        <p className="mt-3 text-sm md:text-base text-foreground/85 leading-relaxed">
          Everything else on a rate page — 22K, 21K, 18K, per gram, per 10
          gram, per tola, per ratti, per sovereign, city variants — is derived
          arithmetic from those two numbers. Nothing is entered manually, which
          is why the site does not carry a "last updated by" human byline on
          individual rate figures: no human touches them.
        </p>

        <H2 id="formula">The formula</H2>
        <div className="mt-3 space-y-2 rounded-xl bg-muted/50 p-4 font-mono text-xs md:text-sm">
          <div>troy ounce = 31.1035 g</div>
          <div>1 tola = 11.6638 g&nbsp; →&nbsp; 1 oz = 2.6667 tola</div>
          <div>PKR 24K per tola = (spot_usd_per_oz ÷ 2.6667) × usd_pkr + premium</div>
          <div>INR 24K per 10 g&nbsp;= (spot_usd_per_oz ÷ 3.11035) × usd_inr + premium</div>
          <div>22K = 24K × (22 ÷ 24) = 24K × 0.9167</div>
          <div>21K = 24K × 0.875&nbsp;&nbsp;&nbsp;&nbsp;18K = 24K × 0.75</div>
        </div>

        <H2 id="worked-example">A worked example you can check</H2>
        <p className="mt-2 text-sm md:text-base text-foreground/85 leading-relaxed">
          Take a spot price of USD 2,400 per troy ounce and an interbank rate
          of PKR 278 per dollar:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm md:text-base text-foreground/85">
          <li>2,400 ÷ 2.6667 = USD 900.01 per tola of pure gold</li>
          <li>900.01 × 278 = PKR 250,203 per tola at interbank</li>
          <li>plus the local market premium → the figure shown as 24K per tola</li>
          <li>22K = 250,203 × 0.9167 ≈ PKR 229,361 per tola (before premium)</li>
        </ul>
        <p className="mt-3 text-sm md:text-base text-foreground/85 leading-relaxed">
          Do that arithmetic against the current spot price and you should land
          within a few hundred rupees of what the page shows. If you land far
          outside that, something is broken — please report it.
        </p>

        <H2 id="premium">Why our rate differs from your local shop</H2>
        <p className="mt-2 text-sm md:text-base text-foreground/85 leading-relaxed">
          This is the single most common question we get, and the honest answer
          is that a jeweller's counter price is not the metal price:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm md:text-base text-foreground/85">
          <li>
            <strong>Open-market vs interbank dollar.</strong> Pakistani sarafa
            markets price against the open-market dollar, which usually trades
            above the interbank rate we convert with. On a stressed currency
            day this alone can be a few thousand rupees per tola.
          </li>
          <li>
            <strong>Making charges (majoori / wastage).</strong> In India and
            Pakistan a jeweller adds a labour charge, typically quoted as a
            percentage of metal value or a flat per-gram fee. It is not part of
            any rate published here.
          </li>
          <li>
            <strong>Tax.</strong> Indian retail bills add GST on top of metal
            plus making charges.
          </li>
          <li>
            <strong>Buy-back is lower than sell.</strong> When you sell gold
            back, expect a deduction — commonly 5–10% in the sarafa market —
            against the quoted rate.
          </li>
        </ul>
        <p className="mt-3 text-sm md:text-base text-foreground/85 leading-relaxed">
          Treat our number as the metal benchmark for that day, and the shop
          number as benchmark plus the shop's costs.
        </p>

        <H2 id="limits">Known limitations</H2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm md:text-base text-foreground/85">
          <li>
            City pages apply a percentage premium over the national rate to
            reflect typical regional spread. That premium is a working estimate
            derived from observed differences, not a live feed from each city's
            association.
          </li>
          <li>
            Weekend and public-holiday figures carry the last traded spot price
            forward. They are not fresh quotes and should not be read as such.
          </li>
          <li>
            We do not forecast tomorrow's price. Pages that once did this have
            been removed, because a fabricated forecast is worse than no
            forecast.
          </li>
          <li>
            Silver is thinner and more volatile than gold; intraday swings of
            several percent are normal and our snapshot can lag a sharp move by
            up to ten minutes.
          </li>
        </ul>

        <H2 id="corrections">Correction policy</H2>
        <p className="mt-2 text-sm md:text-base text-foreground/85 leading-relaxed">
          If a rate is wrong, the fix goes into the pricing pipeline so every
          page that depends on it changes together — we do not hand-patch an
          individual page and leave the rest stale. Report a discrepancy
          through the{" "}
          <Link to="/contact" className="font-medium underline">
            contact page
          </Link>{" "}
          with the city and the figure you were quoted; that detail is what
          makes a premium correction possible.
        </p>

        <H2 id="who">Who publishes this</H2>
        <p className="mt-2 text-sm md:text-base text-foreground/85 leading-relaxed">
          DrHint is an independent bullion-rate and financial-utility site
          covering Pakistan and India. We are not a jeweller, a broker or a
          dealer, and we do not sell gold — so there is no incentive to shade a
          rate in either direction. More about the site's scope and ownership
          is on the{" "}
          <Link to="/about-us" className="font-medium underline">
            about page
          </Link>
          . Nothing published here is investment advice.
        </p>
      </article>
    </Layout>
  );
}
